import type { components } from '@/generated/contracts/api-v1';
import { fieldMap, requireId, type FieldSpec, type LegacyRow } from './fieldMap';

// clip_instances table; ClipInstanceDto.From in lureo-backend Data/Api/V1/SceneContent/ClipInstanceDtos.cs
export type ClipInstanceDto = components['schemas']['ClipInstanceDto'];
export type CreateClipInstanceRequest = components['schemas']['CreateClipInstanceRequest'];

const spec = {
  uniqueId: 'clip_instance_id',
  sceneUniqueId: 'scene_instance_id',
  clipTypeUniqueId: 'clip_type_id',
  assetInstanceUniqueId: 'asset_instance_id',
  uiElementInstanceUniqueId: 'ui_element_instance_id',
  triggerAssetInstanceUniqueId: 'trigger_asset_instance_id',
  triggerUiElementInstanceUniqueId: 'trigger_ui_element_instance_id',
  sourceName: 'clip_instance_source_name',
  displayName: 'clip_instance_display_name',
  category: 'clip_instance_category',
  duration: 'clip_instance_duration',
  beginTime: 'clip_instance_begin_time',
  endTime: 'clip_instance_end_time',
  speed: 'clip_instance_speed',
  volume: 'clip_instance_volume',
  isLocked: 'clip_instance_is_locked',
  keyframeCount: 'clip_instance_keyframe_count',
  keyframeType: 'clip_instance_keyframe_type',
  payload: { legacy: 'clip_instance_payload', as: 'jsonText' },
  settings: { legacy: 'clip_instance_settings', as: 'jsonText' },
  updatedAt: 'updated_at',
} as const satisfies FieldSpec<ClipInstanceDto>;

const map = fieldMap<ClipInstanceDto, typeof spec>('Clip instance', spec);

export type LegacyClipInstance = LegacyRow<typeof spec, ClipInstanceDto>;

/** updateClipInstance / updateClipInstances accept these. */
export const CLIP_INSTANCE_PATCH_FIELDS = [
  'clipTypeUniqueId', 'assetInstanceUniqueId', 'uiElementInstanceUniqueId', 'triggerAssetInstanceUniqueId', 'triggerUiElementInstanceUniqueId',
  'sourceName', 'displayName', 'category', 'duration', 'beginTime', 'endTime', 'speed', 'volume', 'isLocked',
  'keyframeCount', 'keyframeType', 'payload', 'settings',
] as const;
/** createClipInstance accepts these. */
export const CLIP_INSTANCE_CREATE_FIELDS = ['uniqueId', 'sceneUniqueId', ...CLIP_INSTANCE_PATCH_FIELDS] as const;

export const clipInstanceToLegacy = (dto: ClipInstanceDto): LegacyClipInstance => map.toLegacy(dto);

/** The ClipInstanceDto for a legacy row read from v1, for messages that carry the v1 shape. */
export const clipInstanceFromLegacy = (row: LegacyClipInstance): ClipInstanceDto => map.toDto(row);

/** createClipInstance body from a legacy insert row; clip_instance_id is required. */
export function clipInstanceCreateBody(row: Partial<LegacyClipInstance>): CreateClipInstanceRequest {
  const body = map.toBody(row, CLIP_INSTANCE_CREATE_FIELDS, ['updated_at']);
  return { ...body, uniqueId: requireId('Clip instance', 'clip_instance_id', body.uniqueId) };
}

/** updateClipInstance body from a legacy partial update; updated_at is stamped by the server. */
export const clipInstancePatchBody = (changes: Partial<LegacyClipInstance>) =>
  map.toBody(changes, CLIP_INSTANCE_PATCH_FIELDS, ['updated_at']);

/** One item of updateClipInstances (POST /api/v1/clip-instances/batch). */
export const clipInstanceBatchItem = (id: string, changes: Partial<LegacyClipInstance>) => ({ uniqueId: id, ...clipInstancePatchBody(changes) });
